/* eslint-disable no-param-reassign */
import PQueue, { AbortError } from 'p-queue';
import { createAsyncThunk } from '@reduxjs/toolkit';
import getFilesByIds from '../import/helpers/getFilesByIds';
import { updateFile } from '../import/updateFile';
import prepareAssetFields from '../import/helpers/prepareAssetFields';
import uploadFile from './helpers/uploadFile';
import saveNamesToLS from '../import/helpers/saveNamesToLS';

export const startUpload = createAsyncThunk(
  'import/startUpload',
  async (payload, {
    extra: { Logger, localization, Toast }, getState, dispatch,
  }) => {
    let ids = payload;
    if (!ids) {
      ids = Object.values(getState().import.items).reduce((result, group) => {
        result.push(...group.map(({ id, complete }) => (complete ? null : id)).filter(Boolean));
        return result;
      }, []);
    }
    const queue = new PQueue({ concurrency: 5 });
    saveNamesToLS(getState().import.items);
    let inboxUploadLimitErrorsCount = 0;
    /** Add to queue each file */
    ids.forEach(async (id) => {
      /** JOB */
      const controller = new AbortController();

      try {
        const res = await queue.add(async ({ signal }) => {
          const { items } = getState().import;
          const [item] = getFilesByIds(items, [id]);
          const { file, fields } = item;

          /** If already removed from the store */
          if (!item || signal.aborted) return null;

          dispatch(updateFile({
            id,
            fields: {
              controller,
              error: undefined,
            },
          }));

          /** Start uploading */
          try {
            await uploadFile(
              file,
              prepareAssetFields(fields),
              ({ percentage, total }) => {
                dispatch(updateFile({
                  id,
                  fields: {
                    progress: percentage,
                    bytesUploaded: total,
                  },
                }));
              },
              signal,
            );
          } catch (error) {
            const status = error?.error?.status
              ?? error?.status
              ?? error?.response?.status
              ?? error?.data?.status;
            if (status === 402) {
              inboxUploadLimitErrorsCount += 1;
            }
            const { isCancelled } = error;
            /** Upload cancelled by user */
            if (isCancelled) return null;

            /** Error while uploading the file */
            dispatch(updateFile({
              id,
              fields: {
                progress: 0,
                bytesUploaded: 0,
                controller: null,
                error,
              },
            }));

            Logger.info('Error upload asset to Storage: ', error);
            return null;
          }

          dispatch(updateFile({
            id,
            fields: {
              progress: 100,
              bytesUploaded: file.size,
              controller: null,
            },
          }));

          dispatch(updateFile({
            id,
            fields: { complete: true },
          }));
          return true;
        }, { signal: controller.signal });
        return res;
      } catch (error) {
        if (!(error instanceof AbortError)) throw error;
        return null;
      } finally {
        saveNamesToLS(getState().import.items);
      }
    });

    await queue.onIdle();
    const { uploadErrorsCount } = getState().import;

    if (inboxUploadLimitErrorsCount > 0) {
      Logger.log('User', 'InboxLimitsTriggered', { trigger_source: 'upload_limit_reached', user_role: 'none' });
      Toast(localization.IMPORT.inboxAssetsLimitExceeded(inboxUploadLimitErrorsCount), { autoClose: false, type: 'error' });
    }

    /** When queue is empty -> check errors count and if has errors - throw the Error */
    if (uploadErrorsCount > 0) throw new Error(`Has upload errors: ${uploadErrorsCount}`);

    const actualState = getState().import;
    const filesCount = Object.values(actualState.items).flat().length;
    /** Log uploaded files count */
    Logger.log('App', 'UploadFinished', filesCount);
    return null;
  },
);

export const reducer = (builder) => {
  builder.addCase(
    startUpload.pending,
    (state) => {
      state.inProgress = true;
      state.selected = [];
    },
  ).addCase(
    startUpload.fulfilled,
    (state) => {
      state.isUploadSubmitted = false;
      state.inProgress = false;
      saveNamesToLS(state.items);
    },
  ).addCase(
    startUpload.rejected,
    (state) => {
      /** Has errors */
      state.inProgress = false;
    },
  );
};
